import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Save, Plus, Trash2, Send, Lock, Trophy } from 'lucide-react';
import { useRfq, useRfqDetalhe } from '@/features/compras/hooks/useRfq';
import { FornecedorCombobox } from '@/components/FornecedorCombobox';
import { PessoaNome } from '@/components/PessoaNome';
import type { RfqItemRequest, RfqFornecedorRequest } from '@/features/compras/types';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { ProdutoSearchCombobox } from '@/components/ProdutoSearchCombobox';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';

interface ItemLine extends RfqItemRequest { _key: number }

const statusConfig: Record<string, { label: string; className: string }> = {
  Rascunho:   { label: 'Rascunho',    className: 'bg-gray-500/10 text-gray-600'     },
  Enviada:    { label: 'Enviada',     className: 'bg-blue-500/10 text-blue-600'     },
  EmResposta: { label: 'Em Resposta', className: 'bg-yellow-500/10 text-yellow-600' },
  Encerrada:  { label: 'Encerrada',   className: 'bg-orange-500/10 text-orange-600' },
  Adjudicada: { label: 'Adjudicada',  className: 'bg-green-500/10 text-green-600'   },
  Cancelada:  { label: 'Cancelada',   className: 'bg-red-500/10 text-red-600'       },
};

const CotacaoForm = () => {
  const navigate = useNavigate();
  const { id }   = useParams<{ id: string }>();
  const isEdicao = Boolean(id && id !== 'nova');
  const idNum    = isEdicao ? Number(id) : 0;

  const { criarRfq, atualizarRfq, enviarRfq, encerrarRfq, isCreating, isUpdating, isEnviando, isEnccerrando } =
    useRfq();
  const { data: detalhe } = useRfqDetalhe(idNum);

  const [titulo,          setTitulo]          = useState('');
  const [descricao,       setDescricao]       = useState('');
  const [dtPrazo,         setDtPrazo]         = useState('');
  const [itens,           setItens]           = useState<ItemLine[]>([]);
  const [fornecedores,    setFornecedores]    = useState<RfqFornecedorRequest[]>([]);
  const [nextKey,         setNextKey]         = useState(1);
  // Nome dos fornecedores adicionados nesta edição (antes de salvar o DTO não os traz).
  const [nomesForn,       setNomesForn]       = useState<Record<number, string>>({});

  useEffect(() => {
    if (detalhe) {
      setTitulo(detalhe.titulo);
      setDescricao(detalhe.descricao ?? '');
      setDtPrazo(detalhe.dtPrazo?.substring(0, 10) ?? '');
      const linhas: ItemLine[] = detalhe.itens.map((i, idx) => ({
        _key: idx + 1,
        produtoId: i.produtoId,
        descricaoComplementar: i.descricaoComplementar,
        quantidade: i.quantidade,
        unidadeId: i.unidadeId,
        custoEstimado: i.custoEstimado,
      }));
      setItens(linhas);
      setNextKey(linhas.length + 1);
      setFornecedores(detalhe.fornecedores.map(f => ({ fornecedorId: f.fornecedorId })));
    }
  }, [detalhe]);

  const addItem = () => {
    setItens(prev => [...prev, { _key: nextKey, produtoId: 0, quantidade: 1, custoEstimado: 0 }]);
    setNextKey(k => k + 1);
  };

  const removeItem  = (key: number) => setItens(prev => prev.filter(i => i._key !== key));
  const updateItem  = (key: number, field: keyof RfqItemRequest, value: unknown) =>
    setItens(prev => prev.map(i => i._key === key ? { ...i, [field]: value } : i));

  const addFornecedor = (id: number, nome: string) => {
    if (!fornecedores.find(f => f.fornecedorId === id)) {
      setFornecedores(prev => [...prev, { fornecedorId: id }]);
    }
    setNomesForn(prev => ({ ...prev, [id]: nome }));
  };

  const removeFornecedor = (id: number) =>
    setFornecedores(prev => prev.filter(f => f.fornecedorId !== id));

  const handleSalvar = async () => {
    const payload = {
      titulo,
      descricao: descricao || undefined,
      dtPrazo: dtPrazo || undefined,
      itens: itens.map(({ _key: _k, ...rest }) => rest),
      fornecedores,
    };
    if (isEdicao) {
      await atualizarRfq({ id: idNum, data: payload });
    } else {
      const nova = await criarRfq(payload);
      navigate(`/compras/cotacoes/${nova.id}`);
    }
  };

  const podeEditar = !isEdicao || detalhe?.status === 'Rascunho';
  const isSaving   = isCreating || isUpdating;
  const status     = detalhe?.status;
  const statusCfg  = status ? statusConfig[status] : null;

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/compras/cotacoes')}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold">
              {isEdicao ? `RFQ #${detalhe?.numero ?? ''}` : 'Nova Cotação'}
            </h1>
            {statusCfg && <Badge className={statusCfg.className}>{statusCfg.label}</Badge>}
          </div>
        </div>

        <div className="flex gap-2">
          {status === 'Rascunho' && (
            <Button variant="outline" className="gap-2 text-blue-600 border-blue-600"
              disabled={isEnviando}
              onClick={() => enviarRfq(idNum)}>
              <Send className="h-4 w-4" />Enviar Convites
            </Button>
          )}
          {(status === 'Enviada' || status === 'EmResposta') && (
            <Button variant="outline" className="gap-2 text-orange-600 border-orange-600"
              disabled={isEnccerrando}
              onClick={() => encerrarRfq(idNum)}>
              <Lock className="h-4 w-4" />Encerrar
            </Button>
          )}
          {status === 'Encerrada' && (
            <Button variant="outline" className="gap-2 text-green-600 border-green-600"
              onClick={() => navigate(`/compras/cotacoes/${idNum}/comparativo`)}>
              <Trophy className="h-4 w-4" />Comparativo
            </Button>
          )}
        </div>
      </div>

      {/* Cabeçalho */}
      <Card>
        <CardHeader><CardTitle>Identificação</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Título *</Label>
            <Input value={titulo} onChange={e => setTitulo(e.target.value)}
              placeholder="Ex: Cotação de material de escritório" disabled={!podeEditar} />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Prazo de Resposta</Label>
              <Input type="date" value={dtPrazo} onChange={e => setDtPrazo(e.target.value)}
                disabled={!podeEditar} />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Descrição</Label>
            <Textarea value={descricao} onChange={e => setDescricao(e.target.value)}
              rows={2} disabled={!podeEditar} />
          </div>
        </CardContent>
      </Card>

      {/* Fornecedores convidados */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Fornecedores Convidados</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {podeEditar && (
            // USA-COM-10: combobox padrão; o escolhido entra na lista e o campo volta a ficar vazio.
            <FornecedorCombobox
              key={fornecedores.length}
              value={null}
              onSelect={f => f && addFornecedor(f.id, f.descricao)}
              placeholder="Buscar e adicionar fornecedor..."
            />
          )}
          {fornecedores.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-3">Nenhum fornecedor adicionado</p>
          ) : (
            <div className="space-y-2">
              {fornecedores.map(f => {
                const forn = detalhe?.fornecedores.find(df => df.fornecedorId === f.fornecedorId);
                const nome = forn?.nomeFornecedor ?? nomesForn[f.fornecedorId];
                return (
                  <div key={f.fornecedorId} className="flex items-center justify-between p-2 border rounded">
                    <span className="text-sm">{nome ?? <PessoaNome id={f.fornecedorId} />}</span>
                    <div className="flex items-center gap-2">
                      {forn?.respondeu && <Badge className="bg-green-500/10 text-green-600 text-xs">Respondeu</Badge>}
                      {podeEditar && (
                        <Button size="sm" variant="ghost" onClick={() => removeFornecedor(f.fornecedorId)}>
                          <Trash2 className="h-3 w-3 text-red-500" />
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Itens */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Itens Solicitados</CardTitle>
          {podeEditar && (
            <Button size="sm" variant="outline" className="gap-1" onClick={addItem}>
              <Plus className="h-4 w-4" />Adicionar Item
            </Button>
          )}
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="min-w-[260px]">Produto</TableHead>
                <TableHead>Descrição Complementar</TableHead>
                <TableHead className="w-28">Quantidade</TableHead>
                <TableHead className="w-36">Custo Est.</TableHead>
                {podeEditar && <TableHead className="w-12" />}
              </TableRow>
            </TableHeader>
            <TableBody>
              {itens.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-6 text-muted-foreground">
                    Adicione ao menos um item
                  </TableCell>
                </TableRow>
              ) : itens.map(item => (
                <TableRow key={item._key}>
                  <TableCell>
                    <ProdutoSearchCombobox
                      value={item.produtoId || undefined}
                      onChange={pid => updateItem(item._key, 'produtoId', pid)}
                      placeholder="Buscar produto..."
                      disabled={!podeEditar} />
                  </TableCell>
                  <TableCell>
                    <Input value={item.descricaoComplementar ?? ''}
                      onChange={e => updateItem(item._key, 'descricaoComplementar', e.target.value)}
                      placeholder="Complemento..." disabled={!podeEditar} />
                  </TableCell>
                  <TableCell>
                    <Input type="number" step="0.0001" value={item.quantidade}
                      onChange={e => updateItem(item._key, 'quantidade', Number(e.target.value))}
                      disabled={!podeEditar} />
                  </TableCell>
                  <TableCell>
                    <Input type="number" step="0.01" value={item.custoEstimado}
                      onChange={e => updateItem(item._key, 'custoEstimado', Number(e.target.value))}
                      disabled={!podeEditar} />
                  </TableCell>
                  {podeEditar && (
                    <TableCell>
                      <Button size="sm" variant="ghost" onClick={() => removeItem(item._key)}>
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {podeEditar && (
        <div className="flex gap-3">
          <Button onClick={handleSalvar}
            disabled={isSaving || !titulo || itens.length === 0 || fornecedores.length === 0}
            className="gap-2">
            <Save className="h-4 w-4" />{isSaving ? 'Salvando...' : 'Salvar'}
          </Button>
          <Button variant="outline" onClick={() => navigate('/compras/cotacoes')}>Cancelar</Button>
        </div>
      )}
    </div>
  );
};

export default CotacaoForm;
